// Decides which biscuits leave their breadcrumb and render beneath a response
// message instead. Pure (types only) so it lives here in shared/client and stays
// unit-testable; web consumes it from `components/messages/MessageList`.
import type { GetArtifactResp, HeavyArtifactResponse } from "@/types/artifacts.ts";
import { autoMessageTypes, type GetMessageResp, userMessageTypes } from "@/types/messages.ts";
import type { RootItem } from "./eventTree.ts";
import { isReceivedText } from "./inboundText.ts";

/**
 * A biscuit lifted out of its breadcrumb. Carries the message it came from
 * because the artifact query is keyed on (thread, message, artifact) — the
 * biscuit moves, but it still belongs to the message that produced it.
 */
export type AnchoredBiscuit = {
  messageId: number;
  artifact: HeavyArtifactResponse;
};

export type AnchoredBiscuits = {
  /**
   * The biscuits to render beneath each response message, keyed by that
   * message's id and chronological within it. A thread can hold several anchors
   * at once: one per turn that left something pending.
   */
  byAnchorMessageId: ReadonlyMap<number, AnchoredBiscuit[]>;
   /**
   * Every relocated biscuit, anchored or held. The feed renders none of them at
   * their own inline spot — a held biscuit renders nowhere at all, which is the
   * point: it waits for the response rather than appearing above it.
   */
  relocatedIds: ReadonlySet<string>;
};

/** Nothing relocated — the feed renders exactly as it did before. Also what a
 *  caller passes when the feature is gated off. */
export const NO_ANCHORED_BISCUITS: AnchoredBiscuits = {
  byAnchorMessageId: new Map(),
  relocatedIds: new Set(),
};

const AUTO_MESSAGE_TYPES: ReadonlySet<string> = new Set(autoMessageTypes.options);

/**
 * The message types the user actually authored — `userRequest`, plus the
 * `agentRequest` okthx files as an inbound turn. `userMessageTypes` also covers
 * the auto-messages, which render user-side but are records of an action rather
 * than something the user wrote.
 */
const USER_AUTHORED_TYPES: ReadonlySet<string> = new Set(
  userMessageTypes.options.filter((type) => !AUTO_MESSAGE_TYPES.has(type)),
);

/**
 * The synthetic inbound `handleAgentCC` files when okthx is CC'd. It renders as
 * an auto-message, but the server surfaces it to the actor as a user-role turn —
 * because that is what it is: something new arrived to answer.
 */
const INBOUND_AUTO_MESSAGE_TYPE = "autoCommunicationReceived";

/**
 * Whether this message opens a turn. That is the boundary both feed selectors
 * use to end a run: whatever came before it is over, in whatever state it was
 * left.
 *
 * A user request opens one, and so does an inbound. The other auto-messages do
 * not — "You approved and sent an email" records the user acting on a card okthx
 * already produced, so the turn carries on through it.
 */
export const isUserAuthoredMessageType = (messageType: string): boolean =>
  USER_AUTHORED_TYPES.has(messageType) || messageType === INBOUND_AUTO_MESSAGE_TYPE;

/**
 * Whether this message opens a turn: one the user authored, or a text someone
 * sent into the conversation. A received text is what okthx answers, as a
 * user's request is, so it stays where it arrived and keeps its card.
 *
 * A text can arrive while okthx is still working, which a user's own message
 * rarely does (it waits as queued). It ends that run like any boundary: the
 * cards and steps okthx made before it go back to their messages, above the
 * text, and the answer gathers only what came after. Nothing is hidden. The
 * alternative, letting a text pass through a run, would let an inbound on an
 * idle thread re-hide a card from a turn that finished long ago.
 */
export const opensTurn = (message: GetMessageResp) =>
  isUserAuthoredMessageType(message.type) || isReceivedText(message);

/** An auto-message that sits inside a turn rather than opening one — user-side
 *  in the feed, but a record of an action on a card. It neither ends a run nor
 *  folds away with the steps. */
export const isAutoMessageType = (messageType: string): boolean =>
  AUTO_MESSAGE_TYPES.has(messageType) && messageType !== INBOUND_AUTO_MESSAGE_TYPE;

/**
 * Whether a message's heavy artifacts relocate to the turn's answer.
 *
 * Everything except what the user authored. A file attached to a request is
 * their content, and moving it would lift their own photo out of their message
 * and re-home it under okthx's next answer. An auto-message is different: it
 * records an action ("You approved and sent an email") and the artifact it
 * carries is the card okthx produced, so it travels with the rest of the turn.
 */
const hostsRelocatableBiscuits = (messageType: string): boolean =>
  !USER_AUTHORED_TYPES.has(messageType);

/**
 * Whether this artifact's biscuit relocates: every heavy artifact okthx
 * produced, in any state, wherever it is filed.
 *
 * Status-blind on purpose. A card that has been sent or cancelled is still the
 * thing the turn produced, so burying it in the steps drawer while its
 * still-pending siblings sit under the answer would split one turn's output
 * across two places by a distinction the reader does not care about.
 *
 * Note this is a wider set than the omni-artifact's, which shows only what is
 * still awaiting the user (`isActionableArtifact`). The feed shows the whole
 * turn; the omnibox shows what to act on.
 */
export function isRelocatableBiscuit(
  artifact: GetArtifactResp,
  messageType: string,
): artifact is HeavyArtifactResponse {
  return artifact.isHeavy && hostsRelocatableBiscuits(messageType);
}

/**
 * A message that reads as okthx answering the user — what relocated content
 * anchors to. A non-heavy `userResponse` artifact carrying something the user
 * actually sees: prose, or a question form.
 *
 * Both render a footer, which is the real requirement: relocated biscuits sit
 * just above it and the folded steps unroll out of it, so an artifact with
 * neither has nowhere to put them. A bare `userResponse` with no prose and no
 * form is not an answer the user can see, so content ahead of one waits for the
 * turn's real answer instead.
 */
export const isResponseMessage = (artifacts: GetArtifactResp[]): boolean =>
  artifacts.some(
    (artifact) =>
      !artifact.isHeavy &&
      artifact.artifactType === "userResponse" &&
      // A reaction carries an emoji in `response`, but it is drawn on the user's
      // message, not as an answer with a footer of its own.
      artifact.kind !== "reaction" &&
      (!!artifact.response || !!artifact.form),
  );

/**
 * The open biscuits, each relocated beneath the response that explains it: the
 * first response message at or after the message that produced it.
 *
 * Anchoring per-turn rather than to the newest response is what lets a draft
 * left pending from an earlier turn stay where the user last saw it instead of
 * being dragged down the thread every time okthx answers again. The corollary is
 * "hold until the response lands": a biscuit drafted mid-turn has no response
 * after it yet, so it renders nowhere — only its breadcrumb marks it — until the
 * turn actually replies.
 *
 * Holding is only ever provisional, and it ends two ways: a turn opening ends
 * the run before it, and so does the thread going idle. Either way anything
 * still held goes back to the message that produced it.
 *
 * "The thread is running" is a thread-wide fact, so it cannot be the only test:
 * an inbound arriving on an idle thread starts a run, and without a boundary
 * that run would re-hide a card left over from a turn that finished long ago.
 * The boundary is what scopes the hold to the run that is actually working. Without that, a turn
 * that ends without answering — an error, an abort, or simply a thread whose
 * messages predate `userResponse` artifacts — leaves its cards filtered out of
 * the feed and anchored to nothing, which is a drafted email the user can no
 * longer see anywhere.
 *
 * Every biscuit the turn produced travels together, settled or not (see
 * {@link isRelocatableBiscuit}).
 *
 * Walks the built root items rather than raw events so it reads the same feed
 * the fold and the footer pass read — one input, one notion of what is in the
 * thread, no chance of the three disagreeing about which messages exist.
 */
export function selectAnchoredBiscuits(
  rootItems: RootItem[],
  { isTurnRunning }: { isTurnRunning: boolean },
): AnchoredBiscuits {
  const byAnchorMessageId = new Map<number, AnchoredBiscuit[]>();
  const relocatedIds = new Set<string>();
  // Open biscuits seen since the last response — they anchor under the next one.
  let awaitingAnchor: AnchoredBiscuit[] = [];

  for (const item of rootItems) {
    if (item.kind !== "message") continue;
    // An aborted message renders nothing, so it can neither carry a biscuit nor
    // host one.
    const message = item.event.message;
    if (message.status === "aborted") continue;

    if (opensTurn(message)) {
      // A new request ends the previous run. Anything still held never found an
      // answer and never will, so give it back to the message that produced it
      // rather than leaving it relocated to nowhere.
      for (const held of awaitingAnchor) relocatedIds.delete(held.artifact.id);
      awaitingAnchor = [];
      continue;
    }

    const artifacts = message.artifacts ?? [];

    for (const artifact of artifacts) {
      // Dedup guards the session/historical merge surfacing one twice.
      if (!isRelocatableBiscuit(artifact, message.type) || relocatedIds.has(artifact.id)) continue;
      relocatedIds.add(artifact.id);
      awaitingAnchor.push({ messageId: message.id, artifact });
    }

    if (awaitingAnchor.length > 0 && isResponseMessage(artifacts)) {
      byAnchorMessageId.set(message.id, awaitingAnchor);
      awaitingAnchor = [];
    }
  }

  // Whatever is left never found a response after it. Holding it is only
  // justified while a turn is still working — the answer is coming. On an idle
  // thread nothing is coming, so a card held here would be filtered out of its
  // own message and placed nowhere, which is a drafted email the user cannot see
  // anywhere. That is reachable on any turn that ended without answering: an
  // error, an abort, or a thread whose messages predate `userResponse`
  // artifacts.
  if (!isTurnRunning) {
    for (const held of awaitingAnchor) relocatedIds.delete(held.artifact.id);
  }

  //
  // Returning the shared empty value rather than fresh collections matters more
  // than it looks: this runs on every stream delta, and the result reaches every
  // message through context. A new `Map` and `Set` each time invalidates that
  // context on every token, which re-renders the whole thread — including
  // markdown, which re-parses. Most turns relocate nothing, so keeping identity
  // stable in that case removes the churn outright.
  if (relocatedIds.size === 0) return NO_ANCHORED_BISCUITS;
  return { byAnchorMessageId, relocatedIds };
}
